import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { StateHistory } from 'components/DetailsView/StateHistory';
import { Loader, LoaderModeType } from 'components/Loader';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import {
    GetDetailedProductQuery,
    ProductState,
    useUpdateProductStateMutation,
    TotadminPermission,
} from 'data/__generated__';
import { StatusSection } from 'pages/Products/ProductDetails/components/StatusSection';
import { StatusTag } from '../components/StatusTag';
import { confirmPopup } from 'components/Popups/Confirm';

export const StatesSection = ({
    product,
    isDirty,
}: {
    product: NonNullable<GetDetailedProductQuery['detailedProduct']>;
    isDirty: boolean;
}) => {
    const { _id: productId, state, stateHistory } = product;
    const [resetFields, setResetFields] = useState(false);
    const { setValue } = useFormContext();

    useEffect(() => {
        if (resetFields) {
            setValue('isBeingTested', false);
            setValue('isToBeArchived', false);
            setValue('supplierShortageInfo.inShortage', false);
            setValue('supplierShortageInfo.returnDate', null);
            setValue('newUntil', null);
            setResetFields(false);
        }
    }, [resetFields]);

    const [updateProductState, { loading: loadingStateUpdate }] = useUpdateProductStateMutation();

    const handleStateUpdate = async (stateToSet: ProductState) => {
        const { data } = await updateProductState({
            variables: {
                stateToSet,
                productId,
            },
        });
        if (data) {
            const {
                updateProductStateResolver: { success, error, formErrors, product },
            } = data;

            if (success && product) {
                toast.success('Le statut du produit a bien été modifié !');
                if (stateToSet === ProductState.Archived) {
                    setResetFields(true);
                }
            } else if (formErrors?.length) {
                formErrors.forEach(({ sectionName, sectionErrors }) => {
                    toast.error(
                        <span>
                            Erreur dans la section "{sectionName}" :
                            {sectionErrors.map(({ fieldName, fieldError }, index) => (
                                <span key={index}>
                                    <br />- {fieldName} : "{fieldError}"
                                </span>
                            ))}
                        </span>,
                        { autoClose: false },
                    );
                });

                if (formErrors.length > 1) {
                    toast.info('Cliquez pour fermer toutes les notifications', {
                        autoClose: false,
                        onClick: () => toast.dismiss(),
                    });
                }
            } else if (error) {
                toast.error(error, { autoClose: false });
            } else {
                throw Error("Une erreur inconnue s'est produite");
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    };

    const formattedStateHistory = stateHistory.map((stateRecord) => ({
        ...stateRecord,
        state: <StatusTag state={stateRecord.state} />,
    }));

    const STATE_CHANGE_ACTIONS = [
        {
            label: 'Passer produit en statut référencé (Créé)',
            stateToSet: ProductState.Created,
            isDisabled: state === ProductState.Created,
        },
        {
            label: 'Passer produit en statut régulier',
            stateToSet: ProductState.Regular,
            isDisabled: state === ProductState.Regular,
        },
        {
            label: 'Archiver le produit',
            stateToSet: ProductState.Archived,
            isDisabled: state === ProductState.Archived,
        },
    ];

    return (
        <SectionContainer title="Gestion des statuts">
            <ColumnsSectionContainer numberOfColumns={3}>
                <SectionColumn>
                    <StatusTag state={state} showHeading />
                    <StateHistory records={formattedStateHistory} />
                </SectionColumn>
                <SectionColumn>
                    <StatusSection product={product} />
                </SectionColumn>
                {loadingStateUpdate ? (
                    <LoaderContainer>
                        <Loader mode={LoaderModeType.Spin} />
                    </LoaderContainer>
                ) : (
                    <SectionColumn>
                        <>
                            {isDirty ? (
                                <WarningText>
                                    ⚠️ Veuillez vous assurer d'avoir sauvegardé ou annulé vos ajustements avant de
                                    mettre à jour le statut du produit
                                </WarningText>
                            ) : null}
                            <OverlaySectionColumn $isDirty={isDirty}>
                                {STATE_CHANGE_ACTIONS.map((stateChange, index) => {
                                    const { label, stateToSet, isDisabled } = stateChange;

                                    async function updateState(e: React.MouseEvent<HTMLElement, MouseEvent>) {
                                        e.preventDefault();
                                        const hasConfirmed = await confirmPopup({
                                            title: 'Confirmer',
                                            message: `Êtes-vous sûr.e de vouloir ${label.toLowerCase()} ?`,
                                        });
                                        if (hasConfirmed) {
                                            handleStateUpdate(stateToSet);
                                        }
                                    }

                                    return (
                                        <TotemPrimaryButton
                                            requiredPermissions={[TotadminPermission.ProductsManage]}
                                            key={index}
                                            onClick={updateState}
                                            disabled={isDisabled || isDirty}
                                        >
                                            {label}
                                        </TotemPrimaryButton>
                                    );
                                })}
                            </OverlaySectionColumn>
                        </>
                    </SectionColumn>
                )}
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};

const Link = styled.a`
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.ctaPrimaryColor};
`;

const WarningText = styled.div`
    color: ${({ theme }) => theme.textColor};
    font-weight: 800;
`;

const OverlaySectionColumn = styled(SectionColumn)<{ $isDirty: boolean }>`
    opacity: ${({ $isDirty }) => ($isDirty ? 0.4 : 1)};
`;

const LoaderContainer = styled.div`
    flex: 1;
    justify-content: center;
`;
